import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef } from "react";

import { tabsGroupClub, tabsGroupSim } from "../../config/TabsGroup";
import { normalizedKeys } from "../../config/NormalizedValues";
import {
	Club,
	ConvertedData,
	ShotExtra,
} from "../../store/Session/SessionModel";
import sessionSlice from "../../store/Session/SessionSlice";
import { RootState } from "../../store/store";
import { LmLocation } from "../../models";

import "./style.css";
import ShotValueKey from "../DataTargetTable/ShotValueKey";

interface Props {
	dataTableType: "ball-data" | "club-data" | "extra-long-table" | "key-data";
	tabSelected: "ball-data" | "club-data" | "key-data";
	club: Club;
}

const DataTable = ({
	dataTableType,
	tabSelected,
	club,
}: Props): JSX.Element => {
	const dispatch = useDispatch();
	const location = useSelector((state: RootState) => state.session.location);
	const isSimulator = location === LmLocation.SIMULATOR;
	// KWSA-42: Simulator sessions have their own column order. This only renders
	// once the session has loaded — SessionSharing shows a spinner until then.
	const tabs = isSimulator ? tabsGroupSim : tabsGroupClub;

	const getRowStyle = (isSelected: boolean, clubColor: string) => {
		return isSelected
			? {
					border: "2px solid",
					borderColor: clubColor,
					borderRadius: "2px",
				}
			: {};
	};

	const shotRefs = useRef<Record<number, HTMLDivElement | null>>({});

	const latestSelectedShotIndex = club.shots
		.filter((shot) => shot.isSelected)
		.reduce<{ shotIndex: number; ts: number } | null>((latest, shot) => {
			const ts = shot.selectedTimestamp ?? 0;
			if (!latest || ts >= latest.ts) {
				return { shotIndex: shot.shotIndex, ts };
			}
			return latest;
		}, null)?.shotIndex;

	useEffect(() => {
		if (
			latestSelectedShotIndex !== undefined &&
			shotRefs.current[latestSelectedShotIndex]
		) {
			shotRefs.current[latestSelectedShotIndex]!.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
			});
		}
	}, [latestSelectedShotIndex]);

	const setClubShotIsSelected = (shot: ShotExtra) => {
		dispatch(
			sessionSlice.actions.setClubShotIsSelected({
				isSelected: !shot.isSelected,
				clubId: club.clubId,
				shotIndex: shot.shotIndex,
			}),
		);
	};

	const setClubShotIsActive = (
		event: any,
		shot: ShotExtra,
		clubType: string,
	) => {
		event.stopPropagation();
		dispatch(
			sessionSlice.actions.setClubShotIsActive({
				isActive: !shot.isActive,
				clubType: club.clubType,
				shotIndex: shot.shotIndex,
			}),
		);
	};

	return (
		<div className="data-table">
			{club.shots.map((shot: ShotExtra, index: number) => (
				<div
					className="row"
					key={shot.shotIndex}
					ref={(el) => (shotRefs.current[shot.shotIndex] = el)}
					style={getRowStyle(shot.isSelected, club.clubColor)}
					onClick={() => setClubShotIsSelected(shot)}>
					<div className={`is-active-${shot.isActive}`}>
						<div className="icons">
							<div
								onClick={(event) =>
									setClubShotIsActive(event, shot, club.clubType)
								}>
								<img alt="" src="/img/fs_visible-true.svg" />
								{/* { shot.isActive && <img alt="" src="/img/fs_visible-true.svg" /> }
                  { !shot.isActive && <img alt="" src="/img/fs_visible-false.svg" /> } */}
							</div>

							<div className="star-img-wrapper">
								{shot.isFavorite && <img alt="" src="/img/fs_star.svg" />}
							</div>

							<div className="shot-number-text">{index + 1}</div>
						</div>

						<div className="numbers">
							{tabs[tabSelected].map((tabItem: any) => {
								// KWSA-24: Simulator mode shows normalized values in white (no blue highlight).
								const isNormalizedCell =
									shot.isNormalized &&
									normalizedKeys.includes(tabItem.avgKey) &&
									!isSimulator;
								return (
									<div
										className={`text-wrapper ${
											isNormalizedCell ? "normalized" : ""
										}`}
										key={tabItem.shotKey}>
										<ShotValueKey
											shot={
												shot[
													tabItem.shotKey as keyof ShotExtra
												] as ConvertedData
											}
											round={tabItem?.round}
											fixed={tabItem?.fixed}
										/>
									</div>
								);
							})}
						</div>
					</div>
				</div>
			))}
		</div>
	);
};

export default DataTable;
